import { useEffect, useState } from 'react';
import { api } from '../api';

// Per-tab in-memory cache. Keyed by docId; value is a blob URL pointing
// at the cached JPEG bytes. The browser HTTP cache (Cache-Control:
// public, max-age=31536000, immutable on the server side) handles
// cross-tab / cross-session reuse. This map just avoids re-creating
// blob URLs for the same doc within a single session and avoids
// rebuilding them on every grid re-render.
const _cache = new Map();

/**
 * Fetch a document's thumbnail JPEG and expose it as a blob URL for <img src>.
 *
 * Returns { src, error } where `src` is null until the fetch resolves.
 * If hasThumbnail is false, returns { src: null } without ever hitting
 * the network — the grid falls back to its type-icon placeholder.
 *
 * Auth flows through api.raw (Authorization: Bearer …), same as the
 * other binary endpoints (page renders, downloads).
 */
export function useThumbnailUrl(docId, hasThumbnail) {
  const [src, setSrc] = useState(() => (hasThumbnail ? _cache.get(docId) || null : null));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hasThumbnail || !docId) {
      setSrc(null);
      setError(null);
      return;
    }
    const cached = _cache.get(docId);
    if (cached) {
      setSrc(cached);
      setError(null);
      return;
    }

    let cancelled = false;
    setError(null);
    api.raw('GET', `/documents/${docId}/thumbnail`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        _cache.set(docId, url);
        setSrc(url);
      })
      .catch(err => { if (!cancelled) setError(err.message); });

    return () => { cancelled = true; };
  }, [docId, hasThumbnail]);

  return { src, error };
}
